// src/components/AdminAppContent.jsx

import React, { useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// ============================================================
// ADMIN PAGES
// ============================================================
import AdminLoginPage from '../pages/auth/AdminLoginPage';
import AdminRegisterPage from '../pages/admin/AdminRegisterPage';
import AdminDashboard from '../pages/admin/AdminDashboard';

// ============================================================
// ADMIN SLICE ONLY
// ============================================================
import {
  selectIsAuthenticated,
  selectCurrentUser,
  selectAuthLoading,
  adminCheckAuth,
  resetAuthState,
  clearErrors,
} from '../store/slices/adminSlice';

// ============================================================
// COMPONENT
// ============================================================

const AdminAppContent = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const [appReady, setAppReady] = useState(false);
  const authCheckDispatched = useRef(false);

  const isAdminAuthenticated = useSelector(selectIsAuthenticated);
  const adminUser = useSelector(selectCurrentUser);
  const adminLoading = useSelector(selectAuthLoading);

  console.log('🔍 AdminAppContent - Path:', location.pathname);
  console.log('🔍 AdminAppContent - Is Authenticated:', isAdminAuthenticated);
  console.log('🔍 AdminAppContent - Loading:', adminLoading);
  console.log('🔍 AdminAppContent - Admin User:', adminUser);

  // ==========================================================
  // INITIAL ADMIN AUTH CHECK - ONLY RUNS ONCE
  // ==========================================================
  useEffect(() => {
    let mounted = true;

    const initializeAdmin = async () => {
      // Skip if already checked
      if (authCheckDispatched.current) {
        setAppReady(true);
        return;
      }

      try {
        authCheckDispatched.current = true;
        console.log('🔄 Running admin auth check...');
        
        await dispatch(adminCheckAuth()).unwrap();
        console.log('✅ Admin auth check completed');
      } catch (error) {
        console.log('❌ Admin auth check failed:', error);
        dispatch(resetAuthState());
        dispatch(clearErrors());
      } finally {
        if (mounted) {
          setAppReady(true);
        }
      }
    };

    initializeAdmin();

    return () => {
      mounted = false;
    };
  }, [dispatch]);

  // ==========================================================
  // ADMIN ROUTE GUARD
  // ==========================================================
  const AdminRoute = ({ children }) => {
    // If still loading, show loading
    if (adminLoading || !appReady) {
      return (
        <div className="loading-container">
          <div className="loading-content">
            <div className="spinner" />
            <p>Verifying admin access...</p>
          </div>
        </div>
      );
    }

    if (!isAdminAuthenticated) {
      console.log('🔴 Admin not authenticated, redirecting to login');
      return <Navigate to="/admin/login" replace />;
    }

    console.log('🟢 Admin authenticated, allowing access');
    return children;
  };

  // ==========================================================
  // ADMIN LOGIN ROUTE
  // ==========================================================
  const AdminLoginRoute = () => {
    console.log('🔍 AdminLoginRoute - Is Authenticated:', isAdminAuthenticated);
    dispatch(clearErrors());

    if (isAdminAuthenticated) {
      console.log('🟢 Admin already authenticated, redirecting to overview');
      return <Navigate to="/admin/overview" replace />;
    }
    console.log('🔵 Showing AdminLoginPage');
    return <AdminLoginPage />;
  };

  // ==========================================================
  // ADMIN REGISTER ROUTE
  // ==========================================================
  const AdminRegisterRoute = () => {
    if (isAdminAuthenticated) {
      return <Navigate to="/admin/overview" replace />;
    }
    return <AdminRegisterPage />;
  };

  // ==========================================================
  // LOADING
  // ==========================================================
  if (!appReady) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="spinner" />
          <p>Loading admin application...</p>
        </div>
      </div>
    );
  }

  // ==========================================================
  // RENDER - USE RELATIVE PATHS
  // ==========================================================
  return (
    <div className="admin-app-content">
      <Routes>
        {/* ADMIN AUTH - No guards here to prevent redirect loops */}
        {/* Use relative paths - parent route already has /admin */}
        <Route path="login" element={<AdminLoginRoute />} />
        <Route path="register" element={<AdminRegisterRoute />} />

        {/* ADMIN DASHBOARD - Protected */}
        <Route
          path="overview"
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        />

        {/* ADMIN DASHBOARD - Catch all other admin routes */}
        <Route
          path="*"
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        />
      </Routes>
    </div>
  );
};

export default AdminAppContent;